<script setup lang="ts">
import AppNav from "./AppNav.vue";
import LoadingSpinner from "../components/LoadingSpinner.vue";
import UiAlert from "../components/ui/Alert.vue";

import UiButton from "@/components/ui/UiButton.vue";
import UiDropdown from "@/components/ui/Dropdown.vue";
import UiDropdownLink from "@/components/ui/DropdownLink.vue";
import LogoIcon from "@/components/icons/LogoIcon.vue";
import AppContainer from "@/layout/AppContainer.vue";
import { useUserStore } from "@/stores/user.ts";
import { storeToRefs } from "pinia";
import MobileNav from "@/layout/MobileNav.vue";
import { useMenuStore } from "@/stores/menu.ts";
import { useRouter } from "vue-router";
import InlineScript from "@/components/InlineScript.vue";

const userStore = useUserStore();
const { user } = storeToRefs(userStore);

const menuStore = useMenuStore();
const router = useRouter();

function logout() {
  userStore.$auth?.logout({
    logoutParams: { returnTo: window.location.origin },
  });
}

function login() {
  userStore.$auth?.loginWithRedirect();
}

function goToAccount() {
  const nickname = user.value?.nickname ?? "";
  if (nickname) router.push(`/account/${nickname}`);
}
</script>

<template>
  <div
    class="self-stretch py-2 bg-grey inline-flex justify-between items-center overflow-hidden"
  >
    <div class="container flex justify-start items-center lg:gap-7 mx-auto">
      <div class="flex-1 px-5 flex justify-start items-center gap-12">
        <div
          class="justify-center text-xl font-extrabold font-helvetica max-lg:hidden"
        >
          Want to talk to a person?
        </div>
        <a
          href="tel:092810356"
          class="px-6 py-3.5 rounded-[30px] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] flex justify-center items-center gap-2"
        >
          <span
            class="text-center justify-center text-base font-extrabold font-helvetica leading-6 whitespace-nowrap"
          >
            Call 092810356
          </span>
        </a>
      </div>
      <InlineScript
        src="https://cdn.trustindex.io/loader.js?8a88b7f58b3b023f10761f5e79c"
      />
    </div>
  </div>

  <header class="relative">
    <ui-alert
      v-for="alert in menuStore.alerts"
      :key="alert.message"
      :text="alert.message"
      :type="alert.type"
      :link="alert.url"
      :expiration-date="alert.date_finish"
      :start-date="alert.date_start"
    />
    <app-container class="py-9 flex justify-between items-center">
      <router-link to="/">
        <LogoIcon />
      </router-link>

      <nav class="xl:flex hidden items-center gap-x-10">
        <app-nav />

        <loading-spinner
          v-if="userStore.authLoading"
          classes="flex items-center justify-center"
          width="w-8"
          height="h-8"
        />
        <template v-else>
          <ui-dropdown v-if="userStore.isAuthenticated">
            <template #trigger>
              <ui-button
                size="xl"
                outline
                rounded
                class="bg-simply-blank text-white whitespace-nowrap"
              >
                My account
              </ui-button>
            </template>

            <span
              class="block w-full px-4 py-2 text-sm font-medium leading-5 text-left transition text-gray-400 overflow-hidden"
            >
              {{ user?.name }}
            </span>

            <div class="border-t border-gray-100 my-1" />

            <ui-dropdown-link tag="span" @click="goToAccount">
              My account
            </ui-dropdown-link>

            <div class="border-t border-white my-1" />

            <ui-dropdown-link @click="logout">Logout</ui-dropdown-link>
          </ui-dropdown>
          <ui-button
            v-else
            size="xl"
            to="/login"
            outline
            rounded
            @click="login"
          >
            Login
          </ui-button>
        </template>
      </nav>

      <mobile-nav>
        <template v-if="userStore.isAuthenticated">
          <div class="border-t border-gray-100 my-1" />

          <router-link :to="`/account/${user?.nickname ?? ''}`">
            My account
          </router-link>

          <a class="cursor-pointer" @click="logout">Logout</a>
        </template>
        <ui-button v-else size="xl" to="/login" outline rounded @click="login">
          Login
        </ui-button>
      </mobile-nav>
    </app-container>
  </header>
</template>
